// Types for the Agency Fulfilment Call Bot store module (STSMPD-1322).
//
// Mirrors the fulfilment-call-service wire contract (POST/GET /call-requests) as defined in
// agency-fulfilment-call-poc docs/api-contract.md. Service-to-service auth (STSMPD-1316) layers
// on top WITHOUT changing these payloads, so the shapes below are stable.

export type CallContact = {
  phone: string
  label?: string
}

/**
 * The user who triggered the call. `user_id` is a free-form string in the service contract
 * (e.g. "u_1"); ACP sends the numeric admin user id coerced to a string.
 */
export type RequestedBy = {
  user_id?: string
  email: string
  name: string
}

/**
 * Optional but recommended shift context — every field is available on the Agency Shifts row,
 * so ACP sends it to fully populate the server-built script. There is deliberately NO `notice`
 * field: the service derives the notice band server-side from `start_time`.
 */
export type ShiftContext = {
  role_title?: string
  venue_name?: string
  /** ISO 8601 timestamp. */
  start_time?: string
  workers_required?: number
  venue_timezone?: string
}

/** Internal lifecycle status returned by the call service. */
export type CallStatus =
  | 'queued'
  | 'dialing'
  | 'answered'
  | 'no_answer'
  | 'voicemail'
  | 'failed'
  | 'completed'

/** Statuses that mean the call is finished and the modal should show a terminal view. */
export const TERMINAL_CALL_STATUSES: ReadonlyArray<CallStatus> = [
  'completed',
  'no_answer',
  'voicemail',
  'failed',
]

/** ACP-facing call-state label (`last_state` from the service). */
export type CallAlertState =
  | 'Queued'
  | 'On call'
  | 'Answered'
  | 'No answer'
  | 'Voicemail'
  | 'Failed'
  | 'Outcome'

export type OutcomeStatus =
  | 'can_fill'
  | 'partial'
  | 'cannot_fill'
  | 'callback'
  | 'no_answer'
  | 'voicemail'
  | 'failed'

export type CallOutcome = {
  status: OutcomeStatus
  workers_offered?: number
  /** ISO 8601 timestamp. */
  callback_time?: string
  summary?: string
  transcript_url?: string
}

export type CreateCallRequestPayload = {
  job_id: number
  shift_id: number
  agency_id: number
  allocation_id: number
  contact: CallContact
  shift_context?: ShiftContext
  script_id?: string
  requested_by: RequestedBy
  slack_channel?: string
}

/** Response from POST /call-requests (201). */
export type CreateCallResponse = {
  call_request_id: string
  status: CallStatus
}

/** Response from GET /call-requests/:id (200). `outcome` is null until recorded. */
export type CallRequestView = {
  call_request_id: string
  status: CallStatus
  last_state: CallAlertState | null
  outcome?: CallOutcome | null
}
